import { useState, useRef, useCallback, useEffect } from 'react';
import i18n from 'i18next';

export type Subtitle = { text: string; timestamp?: number; color?: string; bgColor?: string };
export type Magnifier = { x: number; y: number; zoom?: number; sizeW?: number; sizeH?: number; size?: number };

export interface UseChunkedVideoRecorderOptions {
  orderNumber: string;
  subtitle?: Subtitle | null;
  magnifier?: Magnifier | null;
  timesliceMs?: number;
  onChunk: (chunk: Blob, seq: number, sessionId: string) => void;
  onSessionEnd?: (sessionId: string, totalChunks: number) => void;
  onError?: (err: Error) => void;
}

export interface SessionMeta {
  sessionId: string;
  totalChunks: number;
}

const ERROR_KEY_BY_NAME: Record<string, string> = {
  NotAllowedError: 'permission_denied',
  NotFoundError: 'camera_not_found',
  NotReadableError: 'camera_in_use',
  OverconstrainedError: 'camera_constraints_unsupported',
  SecurityError: 'camera_security_error',
};

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [ 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm' ];
  return candidates.find(c => MediaRecorder.isTypeSupported(c)) ?? '';
}

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  // Fallback (test envs without crypto.randomUUID)
  const r = (n: number) => [ ...Array(n) ].map(() => Math.floor(Math.random() * 16).toString(16)).join('');
  const yChar = '89ab'[Math.floor(Math.random() * 4)];
  return `${r(8)}-${r(4)}-4${r(3)}-${yChar}${r(3)}-${r(12)}`;
}

export function useChunkedVideoRecorder(options: UseChunkedVideoRecorderOptions) {
  const { orderNumber, subtitle = null, magnifier = null, timesliceMs = 10000, onChunk, onSessionEnd, onError } = options;

  const [ isRecording, setIsRecording ] = useState(false);
  const [ isCameraReady, setIsCameraReady ] = useState(false);
  const [ isPaused, setIsPaused ] = useState(false);
  const [ recordingTime, setRecordingTime ] = useState(0);
  const [ error, setError ] = useState<string | null>(null);
  const [ sessionId, setSessionId ] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasStreamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const seqRef = useRef(0);
  const totalChunksRef = useRef(0);
  const sessionIdRef = useRef<string | null>(null);
  const stopResolversRef = useRef<((meta: SessionMeta | null) => void) | null>(null);
  // Tracks the in-flight or completed openCamera() promise so concurrent calls
  // dedupe to a single getUserMedia, and so startRecording() can await whatever
  // openCamera() is doing instead of racing it.
  const cameraReadyPromiseRef = useRef<Promise<void> | null>(null);

  const orderNumberRef = useRef(orderNumber);
  const subtitleRef = useRef(subtitle);
  const magnifierRef = useRef(magnifier);
  orderNumberRef.current = orderNumber;
  subtitleRef.current = subtitle;
  magnifierRef.current = magnifier;

  const formatTimestamp = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}:${seconds}`;
  };

  const drawVideoFrame = useCallback(() => {
    const video = videoPreviewRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (!video.videoWidth || !video.videoHeight) {
      animationFrameRef.current = requestAnimationFrame(drawVideoFrame);
      return;
    }

    if (canvas.width === 0) {
      const w = video.videoWidth || 1920;
      const h = video.videoHeight || 1200;
      if (w < h) { canvas.width = h; canvas.height = w; }
      else { canvas.width = w; canvas.height = h; }
    }

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw < vh) {
      ctx.save();
      ctx.translate(canvas.width, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(video, 0, 0, vh, vw);
      ctx.restore();
    } else {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    // 1. Magnifier
    const mag = magnifierRef.current;
    if (mag && mag.x != null && mag.y != null) {
      const zoom = mag.zoom || 3;
      const sizeW = mag.sizeW || mag.size || 0.25;
      const sizeH = mag.sizeH ?? null;
      const magW = Math.round(canvas.width * sizeW);
      const magH = sizeH ? Math.round(canvas.height * sizeH) : Math.round(magW * (10 / 16));
      const magX = canvas.width - magW;
      const magY = 0;
      const srcW = magW / zoom;
      const srcH = magH / zoom;
      const srcX = Math.max(0, Math.min(mag.x * canvas.width - srcW / 2, canvas.width - srcW));
      const srcY = Math.max(0, Math.min(mag.y * canvas.height - srcH / 2, canvas.height - srcH));

      if (vw < vh) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(magX, magY, magW, magH);
        ctx.clip();
        ctx.translate(magX, magY);
        ctx.scale(magW / srcW, magH / srcH);
        ctx.translate(-srcX, -srcY);
        ctx.translate(canvas.width, 0);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(video, 0, 0, vh, vw);
        ctx.restore();
      } else {
        ctx.drawImage(video, srcX, srcY, srcW, srcH, magX, magY, magW, magH);
      }

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 3;
      ctx.strokeRect(magX, magY, magW, magH);
      const cx = magX + magW / 2;
      const cy = magY + magH / 2;
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.7)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx - 10, cy); ctx.lineTo(cx + 10, cy);
      ctx.moveTo(cx, cy - 10); ctx.lineTo(cx, cy + 10);
      ctx.stroke();
    }

    // 2. Order number
    if (orderNumberRef.current) {
      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      const w = ctx.measureText(orderNumberRef.current).width;
      ctx.fillRect(10, 10, w + 20, 50);
      ctx.fillStyle = '#fff';
      ctx.fillText(orderNumberRef.current, 20, 45);
    }

    // 3. Timestamp
    const ts = formatTimestamp(new Date());
    ctx.font = 'bold 24px monospace';
    const tw = ctx.measureText(ts).width;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(canvas.width - tw - 30, 10, tw + 20, 50);
    ctx.fillStyle = '#fff';
    ctx.fillText(ts, canvas.width - tw - 20, 45);

    // 4. Subtitle (bottom, fade-out at 3s)
    const sub = subtitleRef.current;
    if (sub && sub.text) {
      const elapsed = Date.now() - (sub.timestamp || 0);
      if (elapsed < 4000) {
        const opacity = elapsed < 3000 ? 1 : 1 - ((elapsed - 3000) / 1000);
        ctx.globalAlpha = opacity;
        const bgH = 60;
        const bgY = canvas.height - bgH;
        ctx.fillStyle = sub.bgColor || 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, bgY, canvas.width, bgH);
        ctx.font = 'bold 32px Arial';
        ctx.fillStyle = sub.color || '#fff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(sub.text, 20, bgY + bgH / 2);
        ctx.globalAlpha = 1;
        ctx.textBaseline = 'alphabetic';
      }
    }

    animationFrameRef.current = requestAnimationFrame(drawVideoFrame);
  }, []);

  const startNewSession = useCallback(() => {
    const id = uuidv4();
    sessionIdRef.current = id;
    setSessionId(id);
    seqRef.current = 0;
    totalChunksRef.current = 0;

    const canvasStream = canvasRef.current!.captureStream(30);
    canvasStreamRef.current = canvasStream;

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(canvasStream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = (e: BlobEvent) => {
      if (!e.data || e.data.size === 0) return;
      const seq = seqRef.current++;
      totalChunksRef.current += 1;
      onChunk(e.data, seq, sessionIdRef.current!);
    };
    recorder.onstop = () => {
      const finishedId = sessionIdRef.current!;
      const finishedTotal = totalChunksRef.current;
      onSessionEnd?.(finishedId, finishedTotal);
      const resolve = stopResolversRef.current;
      stopResolversRef.current = null;
      resolve?.({ sessionId: finishedId, totalChunks: finishedTotal });
    };
    recorder.start(timesliceMs);
    recorderRef.current = recorder;
    return id;
  }, [ onChunk, onSessionEnd, timesliceMs ]);

  // Activates the recording element: opens the camera, wires up the preview
  // <video>, sizes the canvas, and starts the draw loop. Does NOT start a
  // MediaRecorder — that is `startRecording`. Idempotent: concurrent / repeat
  // calls dedupe onto the same getUserMedia.
  //
  // Split from startRecording so a consumer can mount the recorder element
  // (camera live) well before recording begins, giving AE/AWB time to converge
  // and avoiding the cold-start blue/dark first frames that otherwise land at
  // the head of the first recorded video.
  const openCamera = useCallback(async (): Promise<void> => {
    if (cameraReadyPromiseRef.current) {
      return cameraReadyPromiseRef.current;
    }

    const promise = (async () => {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1200 },
          aspectRatio: { ideal: 16 / 10 },
          facingMode: 'environment',
        },
        audio: false,
      });
      streamRef.current = stream;

      if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.muted = true;
        await videoPreviewRef.current.play();
        await new Promise<void>((resolve) => {
          const check = () => videoPreviewRef.current!.videoWidth > 0 ? resolve() : setTimeout(check, 100);
          check();
        });
      }

      // Set canvas dimensions to match video (NOT the default 300x150).
      // Without this, the in-frame guard `canvas.width === 0` never fires
      // (default is 300, not 0) and overlays render on a tiny canvas — making
      // the 32px subtitle font look ~6x larger than intended.
      const canvas = canvasRef.current;
      const vw = videoPreviewRef.current?.videoWidth || 1920;
      const vh = videoPreviewRef.current?.videoHeight || 1200;
      if (vw < vh) {
        canvas.width = vh;
        canvas.height = vw;
      } else {
        canvas.width = vw;
        canvas.height = vh;
      }

      drawVideoFrame();
      setIsCameraReady(true);
    })();

    cameraReadyPromiseRef.current = promise;

    try {
      await promise;
    } catch (e) {
      // Reset so the next call can retry (e.g. user re-grants permission).
      cameraReadyPromiseRef.current = null;
      setIsCameraReady(false);
      const err = e as DOMException;
      const key = ERROR_KEY_BY_NAME[err.name] || 'start_recording_failed';
      const msg = i18n.t(`shared.video_recorder.errors.${key}`, { defaultValue: err.message });
      setError(msg);
      onError?.(err);
      throw err;
    }
  }, [ drawVideoFrame, onError ]);

  const startRecording = useCallback(async () => {
    try {
      // Idempotent: if openCamera was already called (e.g. on component mount),
      // this awaits its promise without re-invoking getUserMedia. If it wasn't,
      // we still open the camera — but the consumer loses the warmup benefit.
      await openCamera();

      startNewSession();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch {
      // openCamera already populated error state and called onError.
    }
  }, [ openCamera, startNewSession ]);

  const pauseRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'paused') {
      recorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    }
  }, []);

  const stopCurrentRecorder = useCallback((): Promise<SessionMeta | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') { resolve(null); return; }
      stopResolversRef.current = resolve;
      try { recorder.requestData(); } catch { /* not all browsers */ }
      recorder.stop();
    });
  }, []);

  const stopRecording = useCallback(async (): Promise<SessionMeta | null> => {
    const meta = await stopCurrentRecorder();
    setIsRecording(false);
    setIsPaused(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoPreviewRef.current) videoPreviewRef.current.srcObject = null;
    cameraReadyPromiseRef.current = null;
    setIsCameraReady(false);
    return meta;
  }, [ stopCurrentRecorder ]);

  const stopAndRestartRecording = useCallback(async (): Promise<SessionMeta | null> => {
    const meta = await stopCurrentRecorder();
    startNewSession();
    return meta;
  }, [ stopCurrentRecorder, startNewSession ]);

  const cancelRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* noop */ }
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoPreviewRef.current) videoPreviewRef.current.srcObject = null;
    cameraReadyPromiseRef.current = null;
    setIsCameraReady(false);
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    setError(null);
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return h > 0
      ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, []);

  useEffect(() => () => { cancelRecording(); }, [ cancelRecording ]);

  return {
    isRecording, isCameraReady, isPaused, recordingTime, error, sessionId,
    videoPreviewRef, canvasRef,
    openCamera,
    startRecording, pauseRecording, resumeRecording,
    stopRecording, stopAndRestartRecording, cancelRecording,
    formatTime,
  };
}
